import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of, firstValueFrom } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from "../../../../environments/environment"

export interface BackendRole {
  id: number;
  titleCode: string | null;
  alias: string;
  isShareholdingStructure: boolean;
}

export interface JobTitle {
  id: number;
  titleCode: string;
  alias: string;

  // Role change metadata (Phase 7)
  appointmentDate?: string;
  removeDate?: string;
  appointmentDocId?: number;
  removalReason?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RoleMappingService {
  private backendRoles: BackendRole[] = [];
  private rolesLoaded$ = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient) {}

  /**
   * Load roles from backend during application initialization
   */
  public loadRoles(): Observable<BackendRole[]> {
    return this.http.get<BackendRole[]>(`${environment.apiExtEndpoint}api/v1/role`).pipe(
      tap(roles => {
        this.backendRoles = roles;
        this.rolesLoaded$.next(true);
      }),
      catchError(error => {
        console.error('Failed to load backend roles:', error);
        this.rolesLoaded$.next(false);
        return of([]);
      })
    );
  }

  /**
   * Check if roles are loaded
   */
  public areRolesLoaded(): Observable<boolean> {
    return this.rolesLoaded$.asObservable();
  }

  /**
   * Map form-engine job titles to backend role IDs
   * @param jobTitles Array of job titles from form-engine with form-engine IDs
   * @returns Array of job titles with backend IDs, or throws error if mapping fails
   */
  public async mapJobTitlesToBackendIds(jobTitles: JobTitle[]): Promise<JobTitle[]> {
    if (!this.backendRoles.length) {
      await firstValueFrom(this.loadRoles());
    }

    const mappedJobTitles: JobTitle[] = [];
    const unmappedTitles: string[] = [];

    for (const jobTitle of jobTitles) {
      const backendRole = this.backendRoles.find(role =>
        role.titleCode === jobTitle.titleCode
      );

      if (backendRole) {
        mappedJobTitles.push({
          ...jobTitle,
          id: backendRole.id,
        });
      } else {
        unmappedTitles.push(jobTitle.titleCode);
      }
    }

    if (unmappedTitles.length > 0) {
      throw new Error(`The following job titles could not be mapped to backend roles: ${unmappedTitles.join(', ')}. Please contact support if this issue persists.`);
    }

    return mappedJobTitles;
  }

  /**
   * Map backend role IDs to form-engine job title IDs
   * @param jobTitles Array of job titles with backend IDs
   * @param formEngineJobPositions Array of form-engine job positions for ID lookup
   * @param entityType "individual" or "bodyCorporate" for context-specific mapping
   * @param sectionId Optional section ID (1 for registration, 100 for post-registration)
   * @returns Array of job titles with form-engine IDs
   */
  public async mapJobTitlesToFrontendIds(
    jobTitles: JobTitle[],
    formEngineJobPositions: any[],
    entityType: string,
    sectionId?: number
  ): Promise<JobTitle[]> {
    if (!this.backendRoles.length) {
      console.warn('Backend roles not loaded, attempting to reload...');
      try {
        await firstValueFrom(this.loadRoles());
      } catch (error) {
        console.error('Failed to load roles for reverse mapping:', error);
        throw new Error('Backend roles not loaded. Please ensure roles are loaded before reverse mapping.');
      }
    }

    // console.log(`🔍 Role mapping: entityType=${entityType}, sectionId=${sectionId}, formEngineJobPositions count=${formEngineJobPositions.length}`);

    const mappedJobTitles: JobTitle[] = [];
    const unmappedTitles: string[] = [];
    const nonCorpStructureRoles = ['APR', 'RAP', 'PAYR', 'PCNT'];

    for (const jobTitle of jobTitles) {
      const backendRole = this.backendRoles.find(role => role.id === jobTitle.id);

      if (!backendRole || !backendRole.titleCode) {
        unmappedTitles.push(`ID ${jobTitle.id}`);
        continue;
      }

      // Skip form-engine mapping for verification/payment roles - keep backend IDs
      if (nonCorpStructureRoles.includes(backendRole.titleCode)) {
        mappedJobTitles.push({
          ...jobTitle,
          titleCode: backendRole.titleCode,
          alias: jobTitle.alias || backendRole.alias
        });
        continue;
      }

      const formEngineJobPosition = formEngineJobPositions.find(pos => {
        const titleMatches = pos.jobTitle === backendRole.titleCode;
        const entityMatches = this.isJobPositionForEntityType(pos, entityType);
        const sectionMatches = sectionId ? pos.sectionId === sectionId : true;
        return titleMatches && entityMatches && sectionMatches;
      });

      if (formEngineJobPosition) {
        mappedJobTitles.push({
          ...jobTitle,
          id: formEngineJobPosition.id,
          titleCode: backendRole.titleCode,
          alias: jobTitle.alias || backendRole.alias
        });
      } else {
        // console.log(`❌ No match for ${backendRole.titleCode}: available positions:`,
        //   formEngineJobPositions.filter(p => p.jobTitle === backendRole.titleCode).map(p => ({
        //     jobTitle: p.jobTitle,
        //     subjectType: p.subjectType,
        //     sectionId: p.sectionId
        //   }))
        // );
        unmappedTitles.push(`${backendRole.titleCode} (${entityType}, section ${sectionId || 'any'})`);
      }
    }

    if (unmappedTitles.length > 0) {
      console.warn(`The following job titles could not be reverse mapped to form-engine IDs: ${unmappedTitles.join(', ')}`);
      for (const jobTitle of jobTitles) {
        if (!mappedJobTitles.find(mapped => mapped.titleCode === jobTitle.titleCode)) {
          const backendRole = this.backendRoles.find(role => role.id === jobTitle.id);
          if (backendRole) {
            mappedJobTitles.push({
              ...jobTitle,
              id: jobTitle.id,
              titleCode: backendRole.titleCode!,
              alias: jobTitle.alias || backendRole.alias
            });
          }
        }
      }
    }

    return mappedJobTitles;
  }

  /**
   * Determine if a job position is for a specific entity type based on section or subject type
   */
  private isJobPositionForEntityType(jobPosition: any, entityType: string): boolean {
    // Check subjectType if available
    if (jobPosition.subjectType) {
      const subjectType = jobPosition.subjectType.toLowerCase();
      if (entityType === 'individual' && subjectType.includes('individual')) {
        return true;
      }
      if (entityType === 'bodyCorporate' &&
          (subjectType.includes('corporate') || subjectType.includes('company'))) {
        return true;
      }
    }

    // Fallback: check section context or assume all job positions are applicable
    // This can be refined based on actual form-engine structure
    return true;
  }

  /**
   * Get backend role by titleCode
   */
  public getBackendRoleByTitleCode(titleCode: string): BackendRole | undefined {
    return this.backendRoles.find(role => role.titleCode === titleCode);
  }

  /**
   * Get all backend roles
   */
  public getBackendRoles(): BackendRole[] {
    return [...this.backendRoles];
  }
}
